import type {
  ColDef,
  FilterChangedEvent,
  GridApi,
  GridOptions,
  PaginationChangedEvent,
  SortChangedEvent,
} from '@ag-grid-community/core';
import type { GridConfig } from '../models/grid-config';
import { useFormat } from './format';

export function useGrid() {
  const { gridDateFormatter, formatGridNumberColumn } = useFormat();

  const gridDateComparator = (filterLocalDateAtMidnight: Date, cellValue: Date) => {
    if (!(cellValue instanceof Date)) {
      return -1;
    }

    /**
     * Date selected from the calendar in dateFilter will have the time as mid night.
     * This will cause problems when using equals filter where the data in the grid
     * have the same date but different time.
     */
    const cellDate = new Date(cellValue.getTime());

    cellDate.setHours(0, 0, 0, 0);

    if (cellDate < filterLocalDateAtMidnight) {
      return -1;
    } else if (cellDate > filterLocalDateAtMidnight) {
      return 1;
    }

    return 0;
  };

  function dateColumnDefinition(definitions: ColDef): ColDef {
    return {
      filter: 'agDateColumnFilter',
      filterParams: {
        maxNumConditions: 1,
        defaultOption: 'inRange',
        inRangeInclusive: true,
        comparator: gridDateComparator,
      },
      valueFormatter: (params) => gridDateFormatter(params),
      ...definitions,
    };
  }

  function numberColumnDefinition(definitions: ColDef): ColDef {
    return {
      valueFormatter: (params) => formatGridNumberColumn(params),
      cellDataType: 'number',
      cellStyle: { textAlign: 'left' },
      ...definitions,
    };
  }

  function defaultGridOptions() {
    const defaultGridOptions: GridOptions<any> = {
      domLayout: 'autoHeight',
      enableCellTextSelection: true,
      rowHeight: 48,
      onPaginationChanged: undefined as undefined | ((event: PaginationChangedEvent<any>) => void),
      onFilterChanged: undefined as undefined | ((event: FilterChangedEvent<any>) => void),
      onSortChanged: undefined as undefined | ((event: SortChangedEvent<any>) => void),
      paginationPageSizeSelector: false,
    };

    return defaultGridOptions as any;
  }

  function applyGridConfig(gridConfig: Partial<GridConfig>, gridApi: GridApi): void {
    if (gridConfig.filters) {
      gridApi.setFilterModel(gridConfig.filters);
    }

    if (gridConfig.pageNumber) {
      gridApi.paginationGoToPage(gridConfig.pageNumber);
    }

    if (gridConfig.sorting) {
      gridApi.applyColumnState({ state: gridConfig.sorting, applyOrder: false });
      gridApi.sizeColumnsToFit();
    }
  }

  return {
    dateColumnDefinition,
    numberColumnDefinition,
    defaultGridOptions,
    applyGridConfig,
  };
}
